import { IPaginationResult } from './Equipment.model';

export type TaskHistoryActionOptions = 'added' | 'removed' | 'changed';

export type TaskHistoryTypeOptions =
  | 'file'
  | 'date'
  | 'priority'
  | 'uncertain'
  | 'status'
  | 'assignee'
  | 'comment'
  | 'title'
  | 'description'
  | 'relationship';

export type TaskStatusOptions = 'todo' | 'inprogress' | 'done';

export type TaskPriorityOptions =
  | 'uncertain'
  | 'non-critical'
  | 'important'
  | 'urgent';

export interface ITaskProduct {
  id: string;
  imageUrl: string;
  name: string;
  model: string;
  manufacturerName: string;
  productPage: string;
  description: string;
}

export interface ITaskHistory {
  id: string;
  action: TaskHistoryActionOptions;
  type: TaskHistoryTypeOptions;
  previousData: string;
  newData: string;
  userName: string;
  userAvatar: string;
  createdAt: Date;
}

export interface ITaskFile {
  id: string;
  filename: string;
  originalname: string;
  createdAt: Date;
  userAvatar: string;
}

export interface ITaskUser {
  id: string;
  name: string;
  avatar: string | null;
}

export interface ITask {
  id: string;
  title: string;
  description: string;
  status: TaskStatusOptions;
  studioId: string;
  priority: TaskPriorityOptions | null;
  assigneeId: string | null;
  dueDate: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ITaskApi {
  id: string;
  title: string;
  description: string;
  status: TaskStatusOptions;
  studioId: string;
  priority: TaskPriorityOptions | null;
  assigneeId: string | null;
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ITaskRawQuery {
  id: string;
  title: string;
  description: string;
  status: TaskStatusOptions;
  priority: TaskPriorityOptions | null;
  dueDate: Date | null;
  createdAt: Date;
  user: ITaskUser | null;
  files: ITaskFile[];
  history: ITaskHistory[];
  products: ITaskProduct[];
}

export interface ITaskApiRawQuery {
  id: string;
  title: string;
  description: string;
  status: TaskStatusOptions;
  priority: TaskPriorityOptions | null;
  dueDate: string | null;
  createdAt: string;
  userId: string | null;
  userName: string | null;
  userAvatar: string | null;
  files: Array<{
    f1: string;
    f2: string;
    f3: string;
    f4: string;
    f5: string;
  }>;
  history: Array<{
    f1: string;
    f2: string;
    f3: string;
    f4: string;
    f5: string;
    f6: string;
    f7: string;
    f8: string;
  }>;
  products: Array<{
    f1: string;
    f2: string;
    f3: string;
    f4: string;
    f5: string;
    f6: string;
    f7: string;
  }>;
}

export default class Task implements ITask {
  public readonly id: string;

  public readonly title: string;

  public readonly description: string;

  public readonly status: TaskStatusOptions;

  public readonly studioId: string;

  public readonly priority: TaskPriorityOptions | null;

  public readonly assigneeId: string | null;

  public readonly dueDate: Date | null;

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  constructor(
    id: string,
    title: string,
    description: string,
    status: TaskStatusOptions,
    studioId: string,
    priority: TaskPriorityOptions | null,
    assigneeId: string | null,
    dueDate: Date | null,
    createdAt: Date,
    updatedAt: Date,
  ) {
    this.id = id;
    this.title = title;
    this.description = description;
    this.status = status;
    this.studioId = studioId;
    this.priority = priority;
    this.assigneeId = assigneeId;
    this.dueDate = dueDate;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
  }

  static fromApiModel(apiModel: ITaskApi) {
    const { assigneeId, description, id, priority, status, studioId, title } =
      apiModel;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));
    const dueDate = apiModel.dueDate
      ? new Date(Date.parse(apiModel.dueDate))
      : null;

    return new Task(
      id,
      title,
      description,
      status,
      studioId,
      priority,
      assigneeId,
      dueDate,
      createdAt,
      updatedAt,
    );
  }

  static arrayFromApiModel(arrayApiModel: ITaskApi[]) {
    const array = arrayApiModel.map(apiModel => this.fromApiModel(apiModel));

    return array;
  }

  static arrayPaginatedFromApiModel(
    arrayApiModel: IPaginationResult<ITaskApi>,
  ) {
    const array = this.arrayFromApiModel(arrayApiModel.results);

    return {
      ...arrayApiModel,
      results: array,
    };
  }

  static fromApiRawQueryModel(apiModel: ITaskApiRawQuery) {
    const {
      description,
      id,
      priority,
      status,
      title,
      userAvatar,
      userId,
      userName,
    } = apiModel;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const dueDate = apiModel.dueDate
      ? new Date(Date.parse(apiModel.dueDate))
      : null;

    const user: ITaskUser | null =
      userId && userName
        ? {
            id: userId,
            name: userName,
            avatar: userAvatar,
          }
        : null;

    const apiModelFiles = apiModel.files || [];
    const apiModelHistory = apiModel.history || [];
    const apiModelProducts = apiModel.products || [];

    const files: ITaskFile[] = apiModelFiles
      .map(item => ({
        id: item.f1,
        filename: `${process.env.REACT_APP_API_URL}/files/${item.f2}`,
        originalname: item.f3,
        createdAt: new Date(Date.parse(item.f4)),
        userAvatar: item.f5,
      }))
      .filter(item => !!item.id);

    const history: ITaskHistory[] = apiModelHistory
      .map(item => ({
        id: item.f1,
        action: item.f2 as TaskHistoryActionOptions,
        type: item.f3 as TaskHistoryTypeOptions,
        previousData: item.f4,
        newData: item.f5,
        createdAt: new Date(Date.parse(item.f6)),
        userName: item.f7,
        userAvatar: item.f8,
      }))
      .filter(item => !!item.id);

    const products: ITaskProduct[] = apiModelProducts
      .map(item => ({
        id: item.f1,
        imageUrl: item.f2,
        name: item.f3,
        model: item.f4,
        manufacturerName: item.f5,
        productPage: item.f6,
        description: item.f7,
      }))
      .filter(item => !!item.id);

    const task: ITaskRawQuery = {
      createdAt,
      dueDate,
      description,
      id,
      priority,
      status,
      title,
      user,
      files,
      history,
      products,
    };

    return task;
  }

  static arrayFromRawQueryModel(arrayApiModel: ITaskApiRawQuery[]) {
    const array = arrayApiModel.map(apiModel =>
      this.fromApiRawQueryModel(apiModel),
    );

    return array;
  }

  static arrayPaginatedFromRawQueryModel(
    arrayApiModel: IPaginationResult<ITaskApiRawQuery>,
  ) {
    const array = this.arrayFromRawQueryModel(arrayApiModel.results);

    return {
      ...arrayApiModel,
      results: array,
    };
  }
}
